import React, { useState, useEffect } from 'react';
import type { AppData, Dossier } from './types';
import { DEMO } from './data/demo';
import { useStorage, useAuth } from './hooks';
import { today, daysUntil, urgencyColor, urgencyLabel, formatDateTime } from './utils/dates';
import { ACCESS_CODE } from './constants';
import { StatCard, Loading, EmptyState, Card, Button } from './components/UI';
import { LoginScreen } from './components/LoginScreen';
import { DossiersList } from './components/DossiersList';
import { CourriersList } from './components/CourriersList';
import { DelaisList } from './components/DelaisList';
import { TachesList } from './components/TachesList';
import { TempsList } from './components/TempsList';
import { HonorairesView } from './components/HonorairesView';
import { FacturesList } from './components/FacturesList';
import { ConventionEditor } from './components/ConventionEditor';

export default function App() {
  const { isAuthenticated, login, logout } = useAuth(ACCESS_CODE);
  const { load, save } = useStorage();

  const [data, setData] = useState<AppData>(DEMO);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    'dashboard' | 'dossiers' | 'delais' | 'taches' | 'temps' | 'honoraires' | 'courriers' | 'factures' | 'convention'
  >('dashboard');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      const loaded = await load();
      if (loaded) setData(loaded);
      setIsLoading(false);
    };
    if (isAuthenticated) loadData();
  }, [isAuthenticated]);

  // Auto-save on data change
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const timer = setTimeout(() => save(data), 500);
      return () => clearTimeout(timer);
    }
  }, [data, isAuthenticated, isLoading]);

  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} accessCode={ACCESS_CODE} />;
  }

  if (isLoading) {
    return <Loading />;
  }

  // Dashboard view
  const nextDeadlines = data.delais
    .sort((a, b) => daysUntil(a.date) - daysUntil(b.date))
    .slice(0, 5);

  const openCases = data.dossiers.filter((d) => d.statut === 'En cours');
  const totalHonoraires = data.dossiers.reduce((sum, d) => sum + d.honoraires, 0);
  const amountPaid = data.dossiers.reduce((sum, d) => sum + d.montantRegle, 0);

  const recentActivities = data.journal.slice(-5).reverse();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: 'var(--color-background-secondary)',
      }}
    >
      {/* Header with navigation */}
      <div
        style={{
          background: 'var(--color-background-primary)',
          borderBottom: '0.5px solid var(--color-border-secondary)',
          padding: '0.75rem 1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Dashboard Avocat</h2>
          <Button onClick={logout} size="sm" variant="secondary">
            Déconnexion
          </Button>
        </div>

        {/* Horizontal navigation */}
        <nav
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '0.25rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '0.5rem',
          }}
        >
          {(
            [
              { id: 'dashboard', label: '📊 Dashboard' },
              { id: 'dossiers', label: '📁 Dossiers' },
              { id: 'delais', label: '⏰ Délais' },
              { id: 'taches', label: '✓ Tâches' },
              { id: 'temps', label: '⏱ Temps' },
              { id: 'honoraires', label: '💰 Honoraires' },
              { id: 'courriers', label: '📬 Courriers' },
              { id: 'factures', label: '📄 Factures' },
              { id: 'convention', label: '⚙️ Convention' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.5rem 0.875rem',
                backgroundColor: activeTab === tab.id ? 'var(--color-background-secondary)' : 'transparent',
                color: activeTab === tab.id ? 'var(--color-accent)' : 'var(--color-text-primary)',
                border: 'none',
                borderRadius: 'var(--border-radius-md)',
                fontSize: 13,
                cursor: 'pointer',
                textAlign: 'center',
                fontWeight: activeTab === tab.id ? 500 : 400,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
        {/* Notification */}
        {notification && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--border-radius-md)',
              backgroundColor:
                notification.type === 'success'
                  ? 'rgba(26, 127, 55, 0.1)'
                  : 'rgba(207, 34, 46, 0.1)',
              color:
                notification.type === 'success'
                  ? 'var(--color-success)'
                  : 'var(--color-danger)',
              fontSize: 13,
            }}
          >
            {notification.message}
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>Dashboard</h1>

            {/* Stats */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem',
              }}
            >
              <StatCard label="Dossiers actifs" value={openCases.length} />
              <StatCard label="Délais à venir" value={nextDeadlines.length} />
              <StatCard label="Honoraires" value={`${totalHonoraires}€`} />
              <StatCard label="Recouvré" value={`${amountPaid}€`} sub={`${Math.round((amountPaid / totalHonoraires) * 100)}%`} />
            </div>

            {/* Next deadlines */}
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '1rem' }}>Prochains délais</h2>
            {nextDeadlines.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                {nextDeadlines.map((delai) => {
                  const dossier = data.dossiers.find((d) => d.id === delai.dossier);
                  const daysLeft = daysUntil(delai.date);
                  return (
                    <Card key={delai.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <p style={{ margin: '0 0 0.25rem', fontSize: 13, fontWeight: 500 }}>
                            {delai.titre}
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            {dossier?.ref} — {dossier?.client}
                          </p>
                        </div>
                        <span
                          style={{
                            background: urgencyColor(daysLeft),
                            color: '#ffffff',
                            padding: '2px 8px',
                            borderRadius: 'var(--border-radius-sm)',
                            fontSize: 11,
                          }}
                        >
                          {urgencyLabel(daysLeft)}
                        </span>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="Aucun délai à venir" />
            )}

            {/* Recent activity */}
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '1rem' }}>Activité récente</h2>
            {recentActivities.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {recentActivities.map((entry) => (
                  <Card key={entry.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ margin: '0 0 0.25rem', fontSize: 13 }}>
                          <strong>{entry.action}</strong> — {entry.detail}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                          {formatDateTime(entry.ts)}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState title="Aucune activité" />
            )}
          </div>
        )}

        {/* Délais tab */}
        {activeTab === 'delais' && (
          <DelaisList data={data} onDataChange={setData} />
        )}

        {/* Tâches tab */}
        {activeTab === 'taches' && (
          <TachesList data={data} onDataChange={setData} />
        )}

        {/* Temps tab */}
        {activeTab === 'temps' && (
          <TempsList data={data} onDataChange={setData} />
        )}

        {/* Honoraires tab */}
        {activeTab === 'honoraires' && (
          <HonorairesView data={data} />
        )}

        {/* Factures tab */}
        {activeTab === 'factures' && (
          <FacturesList data={data} onDataChange={setData} />
        )}

        {/* Convention tab */}
        {activeTab === 'convention' && (
          <ConventionEditor data={data.avocat} onDataChange={(avocat) => {
            const newData = { ...data, avocat };
            newData.journal.push({
              id: `j${Date.now()}`,
              dossier: '',
              action: 'Convention mise à jour',
              detail: `${avocat.nom}`,
              ts: new Date().toISOString(),
            });
            setData(newData);
          }} />
        )}

        {/* Dossiers tab */}
        {activeTab === 'dossiers' && (
          <DossiersList data={data} onDataChange={setData} />
        )}

        {/* Courriers tab */}
        {activeTab === 'courriers' && (
          <CourriersList data={data} onDataChange={setData} />
        )}
      </div>
    </div>
  );
}
